import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatDate, formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';
import { PurchaseOrderStatus } from 'src/models/enums/PurchaseOrderStatus.enum';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls } from 'src/utils/properties.utils';

// ===== Interfaces =====

interface PurchaseOrderViewItem {
  id: string;
  itemName: string;
  requestedQty: number;
  approvedQty: number;
}

interface PurchaseOrderViewData {
  id: string;
  createdAt: string;
  vendorName: string;
  deliveryAddress: string;
  status: string;
  preparedBy: string;
  approvedBy: string | null;
  approvedDate: string | null;
  notes: string | null;
  items: PurchaseOrderViewItem[];
}

interface PurchaseOrderApiItem {
  id: string;
  rawMaterialId: string;
  name: string;
  code?: string;
  requestedQty: number;
  approvedQty: number;
  unitPrice?: number;
}

interface PurchaseOrderApiResponse {
  id: string;
  createdAt: string;
  vendor: {
    organizationName: string;
  };
  deliveryAddress: string;
  status: string;
  preparedBy?: string;
  approvedBy?: string;
  approvedDate?: string | null;
  notes?: string | null;
  purchaseItems: PurchaseOrderApiItem[];
}

// ===== Styles =====

const colors = {
  primary: '#1a3c5e',
  primaryLight: '#3a7bd5',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  orange: '#e67e22',
  blue: '#3a7bd5',
  success: '#27ae60',
  red: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 100,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },

  // Title
  poTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    textAlign: 'center',
    marginBottom: 12,
  },
  poTitleDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 24,
  },

  // Header row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '50%',
  },
  companyLogo: {
    width: 50,
    height: 50,
    marginBottom: 8,
  },
  companyTextBlock: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 2,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'normal',
    marginBottom: 2,
  },
  headerRight: {
    width: '45%',
  },
  poDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  poDetailLabel: {
    fontSize: 9,
    color: colors.textLight,
  },
  poDetailValue: {
    fontSize: 9,
    fontWeight: 'normal',
    color: colors.textDark,
  },
  poIdValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },

  // Section label
  sectionLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: 8,
    letterSpacing: 0.5,
  },

  // Table
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundGray,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    backgroundColor: '#fcfcfc',
  },

  // Column widths for Purchase Order table
  colNum: { width: '8%' },
  colItem: { width: '42%' },
  colItemFull: { width: '67%' },
  colQty: { width: '25%', textAlign: 'center' },
  colApprovedQty: { width: '25%', textAlign: 'center' },

  cellText: {
    fontSize: 9,
    color: colors.textDark,
  },
  cellTextLight: {
    fontSize: 9,
    color: colors.textMedium,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F7FF',
    paddingVertical: 12,
    paddingHorizontal: 40,
  },
  footerText: {
    fontSize: 10,
    color: colors.primary,
    fontWeight: 'bold',
    marginRight: 8,
    letterSpacing: 1,
  },
  footerLogo: {
    width: 60,
    height: 20,
    marginBottom: 2,
  },
});

// ===== PDF Document =====

interface PurchaseOrderPDFProps {
  purchaseOrder: PurchaseOrderViewData;
  companyInfo?: CompanyInfoData;
}

export const PurchaseOrderDocument: React.FC<PurchaseOrderPDFProps> = ({
  purchaseOrder,
  companyInfo,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>POWERED BY</Text>
          <Image style={styles.footerLogo} src={Logo} />
        </View>

        {/* ===== PURCHASE ORDER TITLE ===== */}
        <Text style={styles.poTitle}>PURCHASE ORDER</Text>
        <View style={styles.poTitleDivider} />

        {/* ===== HEADER ROW ===== */}
        <View style={styles.headerRow}>
          {/* Company Info - Left Side */}
          <View style={styles.headerLeft}>
            {companyInfo?.logoUrl && (
              <Image style={styles.companyLogo} src={companyInfo.logoUrl} />
            )}
            <View style={styles.companyTextBlock}>
              <Text style={styles.companyName}>
                {companyInfo?.companyName || 'OrderX'}
              </Text>
              {companyInfo && (
                <>
                  <Text style={styles.companyDetails}>
                    {companyInfo.addressLine1}
                    {companyInfo.addressLine2
                      ? `, ${companyInfo.addressLine2}`
                      : ''}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.city}, {companyInfo.postCode}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.country}
                  </Text>
                  {companyInfo.telephoneNumber && (
                    <Text style={styles.companyDetails}>
                      Tel: {companyInfo.telephoneNumber}
                    </Text>
                  )}
                  {companyInfo.email && (
                    <Text style={styles.companyDetails}>
                      Email: {companyInfo.email}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>

          {/* Purchase Order Details - Right Side */}
          <View style={styles.headerRight}>
            <View style={styles.poDetailRow}>
              <Text style={styles.poDetailLabel}>Date</Text>
              <Text style={styles.poDetailValue}>
                {formatDate(purchaseOrder.createdAt)}
              </Text>
            </View>
            <View style={styles.poDetailRow}>
              <Text style={styles.poDetailLabel}>Vendor</Text>
              <Text style={styles.poDetailValue}>
                {purchaseOrder.vendorName}
              </Text>
            </View>
            <View style={styles.poDetailRow}>
              <Text style={styles.poDetailLabel}>Requested By</Text>
              <Text style={styles.poDetailValue}>
                {purchaseOrder.preparedBy || '-'}
              </Text>
            </View>
            <View style={styles.poDetailRow}>
              <Text style={styles.poDetailLabel}>Approved By</Text>
              <Text style={styles.poDetailValue}>
                {purchaseOrder.approvedBy || '-'}
              </Text>
            </View>
            <View style={styles.poDetailRow}>
              <Text style={styles.poDetailLabel}>Status</Text>
              <Text style={styles.poDetailValue}>
                {purchaseOrder.status || '-'}
              </Text>
            </View>
          </View>
        </View>

        {/* ===== ITEMS TABLE ===== */}
        <Text style={styles.sectionLabel}>DETAILS</Text>
        <View style={styles.table}>
          {/* Table Header */}
          {(() => {
            const hasApprovedQty = purchaseOrder.items.some(
              (item) => item.approvedQty > 0
            );
            return (
              <>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderText, styles.colNum]}>#</Text>
                  <Text
                    style={[
                      styles.tableHeaderText,
                      hasApprovedQty ? styles.colItem : styles.colItemFull,
                    ]}
                  >
                    Items
                  </Text>
                  <Text style={[styles.tableHeaderText, styles.colQty]}>
                    Requested Qty
                  </Text>
                  {hasApprovedQty && (
                    <Text
                      style={[styles.tableHeaderText, styles.colApprovedQty]}
                    >
                      Approved Qty
                    </Text>
                  )}
                </View>

                {/* Table Rows */}
                {purchaseOrder.items.map((item, index) => (
                  <View
                    key={item.id}
                    style={
                      index % 2 === 0 ? styles.tableRow : styles.tableRowAlt
                    }
                  >
                    <Text style={[styles.cellText, styles.colNum]}>
                      {String(index + 1).padStart(2, '0')}
                    </Text>
                    <Text
                      style={[
                        styles.cellText,
                        hasApprovedQty ? styles.colItem : styles.colItemFull,
                      ]}
                    >
                      {item.itemName}
                    </Text>
                    <Text style={[styles.cellTextLight, styles.colQty]}>
                      {item.requestedQty}
                    </Text>
                    {hasApprovedQty && (
                      <Text
                        style={[styles.cellTextLight, styles.colApprovedQty]}
                      >
                        {item.approvedQty > 0 ? item.approvedQty : '-'}
                      </Text>
                    )}
                  </View>
                ))}
              </>
            );
          })()}
        </View>
      </Page>
    </Document>
  );
};

// ===== Generate PDF Function =====

export const generatePurchaseOrderPDF = async (
  purchaseOrderId: string
): Promise<void> => {
  try {
    // Fetch purchase order data
    const response = await axiosTokenInstance.get(
      apiUrls.GET_PURCHASE_ORDERS_BY_ID,
      { params: { id: purchaseOrderId } }
    );
    const poData: PurchaseOrderApiResponse = response.data;

    const purchaseOrderViewData: PurchaseOrderViewData = {
      id: poData.id,
      createdAt: poData.createdAt,
      vendorName: poData.vendor?.organizationName || '-',
      deliveryAddress: poData.deliveryAddress || '-',
      status: poData.status || '-',
      preparedBy: poData.preparedBy || '-',
      approvedBy: poData.approvedBy || null,
      approvedDate: poData.approvedDate ?? null,
      notes: poData.notes ?? null,
      items:
        poData.purchaseItems?.map((item: PurchaseOrderApiItem) => ({
          id: item.id,
          itemName: item.name || '-',
          requestedQty: item.requestedQty || 0,
          approvedQty: item.approvedQty || 0,
        })) || [],
    };

    // Fetch company info
    const companyInfo = await getCompanyInfoForPDF();

    const blob = await pdf(
      <PurchaseOrderDocument
        purchaseOrder={purchaseOrderViewData}
        companyInfo={companyInfo}
      />
    ).toBlob();

    const fileName = `PurchaseOrder-${poData.id}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating Purchase Order PDF:', error);
    throw error;
  }
};

export default PurchaseOrderDocument;
